import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDuration } from '../../../../shared/utils/courseHelper';
import { MdKeyboardArrowDown } from 'react-icons/md';
import { Section } from '../../../../interfaces/screens/content/CourseDetails';
import VideoItem from '../../../../components/videoItem';
import QuizItem from '../../../../components/quizItem';
import { Video } from '../../../../interfaces/api/courses';

/**
 * Content component displays course chapters with their respective videos and quizzes
 * Handles video playback control and tracks current video state
 *
 * @component
 * @example
 * ```tsx
 * <Content
 *   selectedCourse={courseData}
 *   currentVideoId="video-123"
 *   onPlayVideo={handlePlayVideo}
 *   onAssessmentComplete={handleAssessmentComplete}
 *   onNavigateToQuiz={handleNavigateToQuiz}
 * />
 * ```
 */
const Content: React.FC<any> = ({ 
  selectedCourse, 
  currentVideoId, 
  onPlayVideo, 
  onAssessmentComplete, 
  onNavigateToQuiz 
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  /**
   * Toggle section expansion state
   *
   * @param sectionId - ID of the section to toggle
   */
  const toggleSection = (sectionId: string): void => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  // Initialize all sections as expanded
  useEffect(() => {
    if (selectedCourse?.sections) {
      const initial: Record<string, boolean> = {};
      selectedCourse.sections.forEach((section: Section) => {
        initial[section.id] = true;
      });
      setExpandedSections(initial);
    }
  }, [selectedCourse?.sections]);

  /**
   * Determines if a video should be locked based on previous videos completion
   */
  const isVideoLocked = useCallback(
    (sectionIndex: number, videoIndex: number): boolean => {
      if (sectionIndex === 0 && videoIndex === 0) return false; // First video is always unlocked

      const sections = selectedCourse?.sections;
      if (!sections) return true;

      // Check previous videos in the same section
      if (videoIndex > 0) {
        const previousVideo = sections[sectionIndex].videos[videoIndex - 1];
        if (!previousVideo.completed) return true;
      }

      // Check previous section's videos and assessment
      if (videoIndex === 0 && sectionIndex > 0) {
        const previousSection = sections[sectionIndex - 1];
        const allVideosCompleted = previousSection.videos.every((video: any) => video.completed);
        if (!allVideosCompleted) return true;
      }

      return false;
    },
    [selectedCourse?.sections],
  );

  /**
   * Handles navigation to quiz screen - now calls parent handler
   *
   * @param quizData - Data needed for the quiz
   */
  const handleNavigateToQuiz = (quizData: { id: string; finalQuiz?: boolean }): void => {
    onNavigateToQuiz(quizData);
  };

  // Display loading state when course data is not available
  if (!selectedCourse) {
    return (
      <div className="py-4 px-2" aria-label={t('content.loading')}>
        <div className="animate-pulse">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="mb-6">
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-4"></div>
              {[...Array(4)].map((_, j) => (
                <div key={j} className="flex items-center mb-3">
                  <div className="rounded-md bg-gray-200 dark:bg-gray-700 h-10 w-10 mr-3"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-1"></div>
                    <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div role="region" aria-label={t('content.courseContents')}>
      {selectedCourse?.sections.map((section: any, sectionIndex: number) => (
        <div key={section.id} className="border-b dark:border-gray-700">
          <button
            className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
            onClick={() => toggleSection(section.id)}
            aria-expanded={expandedSections[section.id]}
            aria-controls={`section-content-${section.id}`}
          >
            <h2 className="text-base font-bold text-titleText dark:text-gray-100 truncate pr-2">{section.name}</h2>
            <div
              className={`transform transition-transform duration-300 ${expandedSections[section.id] ? 'rotate-180' : 'rotate-0'}`}
            >
              <MdKeyboardArrowDown size={18} className="flex-shrink-0 text-gray-500 dark:text-gray-400" />
            </div>
          </button>

          <div
            id={`section-content-${section.id}`}
            className={`pl-2 grid transition-all duration-300 ease-in-out ${
              expandedSections[section.id] ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
            }`}
          >
            <div className="overflow-hidden">
              {section.videos.map((item: Video, videoIndex: number) => (
                <VideoItem
                  key={item.id}
                  title={item.name}
                  duration={formatDuration(+item.duration)}
                  imageSource={{ uri: item.cover }}
                  onPress={() => onPlayVideo(item.url, item.cover, item.id)}
                  locked={isVideoLocked(sectionIndex, videoIndex)}
                  completed={item.completed}
                  isPlaying={item.id === currentVideoId}
                />
              ))}

              <QuizItem
                locked={!section.videos.every((video: any) => video.completed)}
                title={section.assessment.name}
                completed={false}
                onPress={() =>
                  handleNavigateToQuiz({
                    id: section.id,
                  })
                }
              />
            </div>
          </div>
        </div>
      ))}

      {selectedCourse?.final_assessment && (
        <div className="px-4 py-3">
          <h2 className="text-base font-bold text-titleText dark:text-gray-100 mb-2">
            {t('courseDetails.content.courseFinalQuiz')}
          </h2>

          <QuizItem
            locked={
              !selectedCourse.sections.every((section: any) => section.videos.every((video: any) => video.completed))
            }
            title={selectedCourse.final_assessment.name}
            completed={false}
            onPress={() =>
              handleNavigateToQuiz({
                id: selectedCourse.final_assessment?.id || '',
                finalQuiz: true,
              })
            }
          />
        </div>
      )}

      {/* Modals have been moved to parent CourseDetails component */}
    </div>
  );
};

export default Content;